import {
  myRemindersResponseSchema,
  type NotificationPreference,
  notificationSettingsSchema,
  notificationsPageSchema,
  reminderSchema,
  type SetReminderRequest,
  taskReminderResponseSchema,
  unreadCountSchema,
} from '@wednesday/contracts';
import { infiniteQueryOptions, type QueryClient, queryOptions } from '@tanstack/react-query';

import { apiRequest } from '@/shared/api/client';

/** Server calls of notifications (api-design §3.8). */

export const notificationKeys = {
  all: ['notifications'] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
  list: (unread: boolean) => ['notifications', 'list', { unread }] as const,
  settings: ['notifications', 'settings'] as const,
};

/**
 * NTF-2: the bell refreshes every 30 s and whenever the person comes back to the tab — always stale, so
 * the app-wide 30 s freshness does not swallow the refresh on focus (it is one small request).
 */
export const unreadCountQueryOptions = queryOptions({
  queryKey: notificationKeys.unreadCount,
  queryFn: ({ signal }) =>
    apiRequest('GET', '/notifications/unread-count', { schema: unreadCountSchema, signal }),
  staleTime: 0,
  refetchInterval: 30_000,
  refetchOnWindowFocus: true,
});

export const notificationsQueryOptions = (unread: boolean) =>
  infiniteQueryOptions({
    queryKey: notificationKeys.list(unread),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      apiRequest(
        'GET',
        `/notifications?limit=20${unread ? '&unread=true' : ''}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
        { schema: notificationsPageSchema, signal },
      ),
    getNextPageParam: (last) => last.nextCursor,
  });

export function markRead(notificationId: string) {
  return apiRequest('POST', `/notifications/${notificationId}/read`);
}

export function markAllRead() {
  return apiRequest('POST', '/notifications/read-all');
}

export const settingsQueryOptions = queryOptions({
  queryKey: notificationKeys.settings,
  queryFn: ({ signal }) =>
    apiRequest('GET', '/me/notification-settings', { schema: notificationSettingsSchema, signal }),
});

export function saveSettings(preferences: readonly NotificationPreference[]) {
  return apiRequest('PUT', '/me/notification-settings', {
    body: { preferences },
    schema: notificationSettingsSchema,
  });
}

export function setProjectMuted(projectId: string, muted: boolean) {
  return apiRequest(muted ? 'PUT' : 'DELETE', `/projects/${projectId}/mute`);
}

export async function refreshNotifications(queryClient: QueryClient) {
  await queryClient.invalidateQueries({ queryKey: notificationKeys.all });
}

// ── Remind me (D47, NTF-6, MYW-5) ────────────────────────────────────────────────────────────────

export const reminderKeys = {
  all: ['reminders'] as const,
  mine: ['reminders', 'mine'] as const,
  task: (taskId: string) => ['reminders', 'task', taskId] as const,
};

export const taskReminderQueryOptions = (taskId: string) =>
  queryOptions({
    queryKey: reminderKeys.task(taskId),
    queryFn: ({ signal }) =>
      apiRequest('GET', `/tasks/${taskId}/reminder`, {
        schema: taskReminderResponseSchema,
        signal,
      }),
  });

export const myRemindersQueryOptions = queryOptions({
  queryKey: reminderKeys.mine,
  queryFn: ({ signal }) =>
    apiRequest('GET', '/me/reminders', { schema: myRemindersResponseSchema, signal }),
});

export function setReminder(taskId: string, request: SetReminderRequest) {
  return apiRequest('PUT', `/tasks/${taskId}/reminder`, { body: request, schema: reminderSchema });
}

export function removeReminder(taskId: string) {
  return apiRequest('DELETE', `/tasks/${taskId}/reminder`);
}
